/// <amd-module name="Repos/Tfvc/Views/ChangesetDetails/Util" />
import { IGitCommitChange } from "Repos/Changes/Contracts";
import { IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { getChangesCollection } from "Repos/Changes/Util";
import { Change, TfvcVersionDescriptor, TfvcVersionOption, TfvcVersionType } from "Repos/Common/Contracts/Git";
import { getParentPath } from "Repos/Common/Util/File";
import { getTfvcVersionString } from "Repos/Common/Util/Version";
import { ITfvcChangeset } from "./Contracts";

/**
 * Identifies empty folders in the given set of changes.
 *
 * @param changes List of changes.
 */
function getEmptyFolders(changes: Change<ITfvcChangeset>[]): { [path: string]: boolean } {
    const emptyFoldersMap: { [path: string]: boolean } = {};
    for (const change of changes) {
        const { isFolder, serverItem } = change.item;
        if (isFolder) {
            emptyFoldersMap[serverItem] = true;
        }

        const parentPath = getParentPath(serverItem, "$/") || "";
        // Remove parent path (folder path) from empty paths
        delete emptyFoldersMap[parentPath];
    }

    return emptyFoldersMap;
}

export function getTfvcChangesCollection(changes: Change<ITfvcChangeset>[], changesetId: number): IChangeCollection<TfvcVersionDescriptor> {
    const convertedChanges: IGitCommitChange[] = [];

    // Getting the list of empty folders since they're valid items for TFVC
    // which are displayed as a separate item in a changeset or shelveset
    const emptyFoldersMap = getEmptyFolders(changes);

    // Convert TFVC change to IGitChangeItem
    for (const tfvcChange of changes) {
        const { changeType, item, sourceServerItem } = tfvcChange;
        const { isFolder, isSymLink, serverItem, version } = item;

        convertedChanges.push({
            changeType,
            commitId: version,
            item: {
                isEmptyFolder: emptyFoldersMap[serverItem], // empty folders are allowed for TFVC
                isFolder: isFolder,
                isSymLink: isSymLink,
                path: serverItem,
                originalPath: sourceServerItem,
                trackingId: 0
            }
        });
    }

    const sourceVersion = { version: changesetId.toString(), versionOption: TfvcVersionOption.None, versionType: TfvcVersionType.Changeset };
    const targetVersion = { version: changesetId.toString(), versionOption: TfvcVersionOption.Previous, versionType: TfvcVersionType.Changeset };
    const changesCollection = getChangesCollection<TfvcVersionDescriptor>(
        convertedChanges,
        "",
        getTfvcVersionString(sourceVersion),
        sourceVersion,
        targetVersion,
        undefined,
        undefined,
        undefined,
        "$"
    );

    // Tweak changes collection since it's designed for git changes
    const { rootTreeItem } = changesCollection;
    const { childItems = [] } = rootTreeItem;
    if (childItems.length > 0) {
        // Since the root item is git-repo item, we need to make its child the root and eliminate git-repo item
        const tfvcRootItem = childItems[0];
        tfvcRootItem.data.name = "$/" + tfvcRootItem.data.name;
        changesCollection.rootTreeItem = tfvcRootItem;

        const { path = "" } = tfvcRootItem.data;
        const pathParts = path.split("/");
        if (pathParts.length <= 2) {
            tfvcRootItem.data.iconProps = { iconName: "TFVCLogo", className: "repos-folder-icon" };
        }
    } else {
        // There is only root item, we need to change name and logo in this case
        rootTreeItem.childItems = undefined;
        const { data } = rootTreeItem;
        data.name = "$";
        data.iconProps = { iconName: "TFVCLogo", className: "repos-folder-icon" };
    }

    return changesCollection;
}
